import { Component, OnInit, OnDestroy, HostListener, ElementRef, ViewChild, ChangeDetectorRef } from '@angular/core';
import { Subject, EMPTY, Observable, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, takeUntil, tap, finalize } from 'rxjs/operators';
import { GlobalSearchService, GroupedSearchResults, SearchResultItem } from '../../services/global-search.service';
import { Router } from '@angular/router';
import { NavigationReloadService } from '../../services/navigation-reload.service';
import { SearchHistoryService } from '../../services/search-history.service';

@Component({
  selector: 'app-global-search',
  templateUrl: './global-search.component.html',
  styleUrls: ['./global-search.component.scss'],
})
export class GlobalSearchComponent implements OnInit, OnDestroy {
  @ViewChild('searchInput') searchInputRef!: ElementRef<HTMLInputElement>;
  @ViewChild('searchModalDiv') searchModalRef!: ElementRef<HTMLDivElement>;
  @ViewChild('historyContainer') historyContainerRef!: ElementRef<HTMLDivElement>;

  isVisible = false;
  searchTerm = new Subject<string>();
  results: GroupedSearchResults | null = null;
  isLoading = false;
  isApiLoading = false;
  private destroy$ = new Subject<void>();
  activeItemIndex: number = -1;
  flatResults: SearchResultItem[] = [];
  recentSearches$: Observable<string[]>;
  displayedHistory: string[] = [];
  showHistory = false;
  private historySubscription: Subscription | null = null;
  currentSearchTermValue: string = '';
  private searchTermSubscription: Subscription | null = null;

  get displayedResultGroups(): string[] {
    if (!this.results) return [];
    const keys = Object.keys(this.results);
    
    // Remove empty groups before sorting, except for 'Pages' if it exists (even if empty initially)
    // or if it is API_Error
    const filteredKeys = keys.filter(key => 
      (this.results && this.results[key] && this.results[key].length > 0) || key === 'Pages' || key === 'API_Error'
    );

    return filteredKeys.sort((a, b) => {
      if (a === 'Pages') return -1;
      if (b === 'Pages') return 1;
      if (a === 'API_Error') return 1; // API_Error towards the end if it occurs
      if (b === 'API_Error') return -1;
      // Optional: Alphabetical sort for other groups
      return a.localeCompare(b);
    });
  }

  constructor(
    private globalSearchService: GlobalSearchService,
    private router: Router,
    private elementRef: ElementRef,
    private cdr: ChangeDetectorRef,
    private navigationReloadService: NavigationReloadService,
    private searchHistoryService: SearchHistoryService
  ) {
    this.recentSearches$ = this.searchHistoryService.history$;
  }

  ngOnInit(): void {
    this.historySubscription = this.recentSearches$.subscribe(history => {
        this.displayedHistory = history.slice(0, 5);
        if (this.showHistory && this.activeItemIndex >= this.displayedHistory.length) {
            this.activeItemIndex = -1;
        }
        this.cdr.detectChanges();
    });

    this.searchTermSubscription = this.searchTerm.subscribe(term => {
      this.currentSearchTermValue = term;
    });

    this.globalSearchService.requestSearchModalOpen$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        console.log('[GlobalSearchComponent] Received open request from service');
        if (!this.isVisible) {
          this.openSearch();
        }
      });

    this.searchTerm
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap(term => {
            const historyVisible = !term;
            if (this.showHistory !== historyVisible) {
                this.activeItemIndex = -1;
                this.showHistory = historyVisible;
            }
            // If term becomes empty, also reset loading states and results immediately
            if (!term) {
                this.results = null;
                this.flatResults = [];
                this.activeItemIndex = -1;
                this.isLoading = false;
                this.isApiLoading = false;
            }
            this.cdr.detectChanges(); // For history visibility and empty term reset
        }),
        switchMap((term) => {
          if (!term) {
            return EMPTY;
          }
          this.activeItemIndex = -1;
          this.flatResults = [];
          this.searchHistoryService.addSearchTerm(term);
          this.isLoading = true;
          this.isApiLoading = true;
          console.log(`[GlobalSearchComponent] switchMap - term: "${term}", isLoading: ${this.isLoading}, isApiLoading: ${this.isApiLoading}`); // DEBUG
          this.cdr.detectChanges();

          return this.globalSearchService.search(term).pipe(
            tap(results => console.log('[GlobalSearchComponent] Emitted from search service:', JSON.stringify(results))), // DEBUG
            finalize(() => {
              this.isApiLoading = false;
              this.isLoading = false;
              console.log(`[GlobalSearchComponent] finalize - isLoading: ${this.isLoading}, isApiLoading: ${this.isApiLoading}`); // DEBUG
              this.cdr.detectChanges();
            })
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
          next: (resultsData) => { 
            console.log('[GlobalSearchComponent] subscribe next - resultsData:', JSON.stringify(resultsData)); // DEBUG
            this.results = resultsData;
            this.updateFlatResults();   // Recalculate flat results based on new results
            // isLoading and isApiLoading are handled by tap/finalize, but intermediate CDR for results is good.
            this.cdr.detectChanges();
          },
          error: (err) => { // This is for errors from searchTerm or switchMap chain itself
              console.error('Search stream error:', err);
              this.isLoading = false;
              this.isApiLoading = false;
              this.results = { 'Error': [{id: 'stream-error', label: 'Search failed.', type: 'error'}] };
              this.cdr.detectChanges();
          }
      });
  }

  updateFlatResults(): void {
    this.flatResults = [];
    if (this.results) {
      for (const groupName of this.displayedResultGroups) {
        if (this.results[groupName]) {
          this.flatResults.push(...this.results[groupName]);
        }
      }
    }
  }

  isResultItemActive(item: SearchResultItem): boolean {
    if (this.showHistory || this.activeItemIndex === -1 || this.flatResults.length === 0) {
      return false;
    }
    return this.flatResults[this.activeItemIndex] === item;
  }

  isHistoryItemActive(index: number): boolean {
      return this.showHistory && this.activeItemIndex === index;
  }

  onSearchInput(event: Event): void {
    const term = (event.target as HTMLInputElement).value;
    this.searchTerm.next(term);
  }

  openSearch(): void {
    console.log('[GlobalSearchComponent] openSearch method called');
    this.isVisible = true;
    this.activeItemIndex = -1;
    this.flatResults = [];
    this.showHistory = !this.searchInputRef?.nativeElement.value;
    setTimeout(() => this.searchInputRef?.nativeElement.focus(), 0);
  }

  closeSearch(): void {
    console.log('[GlobalSearchComponent] closeSearch method called');
    this.isVisible = false;
    this.searchTerm.next('');
    this.results = null;
    this.activeItemIndex = -1;
    this.flatResults = [];
    this.showHistory = false;
    this.cdr.detectChanges();
  }
  
  handleEnterKey(): void {
    if (this.showHistory) {
        if (this.activeItemIndex !== -1 && this.displayedHistory[this.activeItemIndex]) {
            this.searchFromHistory(this.displayedHistory[this.activeItemIndex]);
        }
    } else {
        if (this.activeItemIndex !== -1 && this.flatResults[this.activeItemIndex]) {
            this.navigateTo(this.flatResults[this.activeItemIndex]);
        } else if (this.results && this.displayedResultGroups.length > 0) {
            const firstGroupKey = this.displayedResultGroups[0];
            if (this.results[firstGroupKey] && this.results[firstGroupKey].length > 0) {
                this.navigateTo(this.results[firstGroupKey][0]);
            }
        }
    }
  }

  navigateTo(item: SearchResultItem): void {
    let navigationPath: string | null = null;
    console.log('Attempting navigation for item:', item);

    switch (item.type) {
      case 'page':
        if (item.route) {
          navigationPath = item.route;
        } else {
          console.warn('Page item is missing a route:', item);
        }
        break;
      case 'MTShipstationOrder':
        navigationPath = `/shipping/posted-orders/${item.id}`;
        break;
      case 'RequestForQuote':
        navigationPath = `/inventory/${item.id}/view`;
        break;
      case 'Picklist':
        navigationPath = `/shipping/picklists/${item.id}`;
        break;
      case 'MTPurchaseOrder':
        navigationPath = `/orders/${item.id}`;
        break;
      case 'AssemblyOrder':
        navigationPath = `/assembly-orders/${item.id}/edit`;
        break;
      default:
        console.warn(`Unhandled search item type for navigation: ${item.type}`, item);
        break;
    }

    if (navigationPath) {
      // *** SET THE FLAG BEFORE NAVIGATING ***
      this.navigationReloadService.setForceReload(true);
      console.log('Navigating (force reload set) to path:', navigationPath);
      this.router.navigate([navigationPath]);
    } else {
      console.error('No navigation path determined for:', item);
    }
    this.closeSearch();
  }

  @HostListener('keydown.arrowdown', ['$event'])
  onArrowDown(event: KeyboardEvent): void {
    if (!this.isVisible) return;
    event.preventDefault();

    if (this.showHistory) {
        if (this.displayedHistory.length === 0) return;
        this.activeItemIndex++;
        if (this.activeItemIndex >= this.displayedHistory.length) {
            this.activeItemIndex = 0;
        }
    } else {
        if (this.flatResults.length === 0) return;
        this.activeItemIndex++;
        if (this.activeItemIndex >= this.flatResults.length) {
            this.activeItemIndex = 0;
        }
    }
    this.cdr.detectChanges();
    this.ensureActiveItemVisible();
  }

  @HostListener('keydown.arrowup', ['$event'])
  onArrowUp(event: KeyboardEvent): void {
    if (!this.isVisible) return;
    event.preventDefault();

    if (this.showHistory) {
        if (this.displayedHistory.length === 0) return;
        this.activeItemIndex--;
        if (this.activeItemIndex < 0) {
            this.activeItemIndex = this.displayedHistory.length - 1;
        }
    } else {
        if (this.flatResults.length === 0) return;
        this.activeItemIndex--;
        if (this.activeItemIndex < 0) {
            this.activeItemIndex = this.flatResults.length - 1;
        }
    }
    this.cdr.detectChanges();
    this.ensureActiveItemVisible();
  }

  ensureActiveItemVisible(): void {
    let activeElement: Element | null = null;
    if (this.showHistory) {
      activeElement = this.elementRef.nativeElement.querySelector(`.lum-c-global-search__history-item--active`);
    } else {
      activeElement = this.elementRef.nativeElement.querySelector(`.lum-c-global-search__result-item--active`);
    }
    if (activeElement) {
      activeElement.scrollIntoView({ block: 'nearest' });
    }
  }

  @HostListener('document:keydown.control.k', ['$event'])
  @HostListener('document:keydown.meta.k', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    event.preventDefault();
    if (this.isVisible) {
      this.searchInputRef?.nativeElement.focus();
    } else {
      this.openSearch();
    }
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscape(event: KeyboardEvent): void {
    if (this.isVisible) {
      this.closeSearch();
    }
  }
  
  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent) {
    if (this.isVisible && this.searchModalRef && !this.searchModalRef.nativeElement.contains(event.target as Node)) {
      this.closeSearch();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.historySubscription) {
        this.historySubscription.unsubscribe();
    }
    if (this.searchTermSubscription) {
        this.searchTermSubscription.unsubscribe();
    }
  }

  searchFromHistory(term: string): void {
    if (this.searchInputRef) {
      this.searchInputRef.nativeElement.value = term;
    }
    this.showHistory = false;
    this.searchTerm.next(term);
  }
} 